import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/components/Alert';
import { AuthShell } from '@/shared/components/AuthShell';
import { Button } from '@/shared/components/Button';
import { TextField } from '@/shared/components/Field';
import { resolveErrorMessage } from '@/shared/components/States';
import { OrganisationChoice, OrganisationChoiceExpired } from './OrganisationChoice';
import {
  isOrganisationChoice,
  isSignInStep,
  type OrganisationSelection,
} from './organisationChoice';
import { useLogin } from './useSession';

/**
 * Where the sign-in is: the address and password (step 1), the organisation
 * to open when they open more than one (step 2), or a step 2 that can no
 * longer continue. Held here and nowhere else — not the session cache, the URL
 * or storage — so a reload is always step 1.
 */
type Stage =
  | { kind: 'credentials' }
  | { kind: 'choose'; selection: OrganisationSelection }
  | { kind: 'expired' };

/**
 * Sign-in.
 *
 * Step 1 asks for the address and the password and nothing else: when an
 * address belongs to more than one organisation, the server offers the ones
 * this password opens, and step 2 (`OrganisationChoice`) asks which. Every
 * step stands in the sign-in family's frame (`AuthShell`), so moving from the
 * password to the organisation to the MFA step never changes the page.
 *
 * Deliberately built on plain state and native validation rather than the
 * schema-validation stack the rest of the app uses. This screen is part of the
 * initial download for every visitor, and pulling in a form library plus a
 * schema validator for two required fields would cost ~27 kB gzipped before a
 * user has even authenticated. Richer forms live behind lazily loaded routes,
 * where that cost is paid only by the people who open them.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const login = useLogin();

  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  // Why step 2 ended, when the chosen organisation refused: said on step 1.
  const [refusal, setRefusal] = useState<unknown>(null);
  // Back at step 1 with the address kept: the password is where to continue.
  const [returned, setReturned] = useState(false);

  const emailInvalid = touched && email.trim() === '';
  const passwordInvalid = touched && password === '';

  /** Back to step 1: the address kept, the password empty. */
  const backToCredentials = (reason: unknown = null) => {
    setPassword('');
    setTouched(false);
    setRefusal(reason);
    setReturned(true);
    setStage({ kind: 'credentials' });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (email.trim() === '' || password === '') return;

    setRefusal(null);
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (result) => {
          // A session: `useLogin` has cached it and the gate takes over.
          if (!isSignInStep(result)) return;

          // A step opens no session. Nothing of step 1 is kept past it — not
          // the password, and not the answer (with its token) in the mutation.
          login.reset();
          setPassword('');
          setTouched(false);

          if (isOrganisationChoice(result)) {
            setStage({ kind: 'choose', selection: result.selection });
          } else {
            setRefusal(new Error('An unreadable sign-in step'));
          }
        },
      },
    );
  };

  if (stage.kind === 'choose') {
    return (
      <OrganisationChoice
        email={email.trim()}
        selection={stage.selection}
        onBack={() => backToCredentials()}
        onExpired={() => setStage({ kind: 'expired' })}
        onRefused={(reason) => backToCredentials(reason)}
      />
    );
  }

  if (stage.kind === 'expired') {
    return <OrganisationChoiceExpired onRestart={() => backToCredentials()} />;
  }

  const failure: unknown = refusal ?? login.error;

  return (
    <AuthShell identity="tenant" title={t('auth.welcome')} subtitle={t('auth.signInSubtitle')}>
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {failure ? <Alert tone="danger">{resolveErrorMessage(failure, t)}</Alert> : null}

        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="username"
          required
          value={email}
          error={emailInvalid ? t('errors.VALIDATION_FAILED') : undefined}
          onChange={(event) => setEmail(event.target.value)}
        />

        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          autoFocus={returned}
          value={password}
          error={passwordInvalid ? t('errors.VALIDATION_FAILED') : undefined}
          onChange={(event) => setPassword(event.target.value)}
        />

        <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
          {login.isPending ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>
    </AuthShell>
  );
}
